import { Ban, CheckCircle2, ListChecks, TriangleAlert, UserMinus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { attritionCategoriesApi } from '../attritionCategoriesApi';
import type { AttritionCategoryDetail } from '../attritionCategoryTypes';
import { AttritionCategoryModal } from '../components/AttritionCategoryModal';

type TabKey = 'info';

/**
 * One attrition category and its checklist.
 *
 * On the **header** layout: a category reads as one continuous thing — a name, the documents it
 * asks for with who reviews each, and the trail. **One card, one modal**: the checklist is edited
 * on the category's own form, so the Checklist card carries the same Edit as Details would — it is
 * PROMOTED to the header rather than offered twice.
 */
export function AttritionCategoryProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [category, setCategory] = useState<AttritionCategoryDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState<'edit' | 'delete' | 'restore' | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      attritionCategoriesApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setCategory(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this category.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Attrition',
    { label: 'Settings', to: '/attrition/settings' },
    { label: 'Categories', to: '/attrition/settings/categories' },
    category?.name ?? 'Category',
  ];

  // Guarded on having NOTHING to show — a failed refresh keeps the record and goes to the banner.
  if (error && !category) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ProfileHeaderSkeleton tabs={1} />
      </div>
    );
  }

  const record = category;
  const stranded = record.documents.filter((d) => !d.reviewerIsActive);

  // The banner's way out when the checklist cannot be filed against — it only OPENS the form,
  // whose own tick is the gate. Hidden, not disabled, without the key.
  const editChecklist = (
    <PermissionGate permission="attrition-categories.update">
      <Button
        id="attrition-category-profile-edit-checklist"
        variant="secondary"
        leading={<ListChecks className="h-4 w-4" aria-hidden="true" />}
        onClick={() => setPending('edit')}
      >
        Edit checklist
      </Button>
    </PermissionGate>
  );

  async function runConfirmed() {
    setRunning(true);
    setActionError(null);

    try {
      if (pending === 'delete') await attritionCategoriesApi.deactivate(record.id);
      else await attritionCategoriesApi.activate(record.id);

      setPending(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="attrition-category-profile"
        header={
          <ProfileHeader
            id="attrition-category-profile-header"
            icon={<UserMinus className="h-6 w-6" aria-hidden="true" />}
            title={record.name}
            meta={[
              `${record.documents.length} ${record.documents.length === 1 ? 'document' : 'documents'}`,
              `${record.requestCount} ${record.requestCount === 1 ? 'request' : 'requests'}`,
            ]}
            status={
              <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                {record.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            // No Edit here: the Details card's cog owns the one modal, and a record offers it once.
            settings={{
              onHistory: () => setHistoryOpen(true),
              menuItems: record.isActive
                ? []
                : [
                    {
                      key: 'restore',
                      label: 'Restore',
                      permission: 'attrition-categories.delete',
                      onClick: () => setPending('restore'),
                    },
                  ],
              onDelete: () => setPending('delete'),
              deletePermission: 'attrition-categories.delete',
              deleteDisabledReason: record.isActive ? null : 'This category is already deleted.',
            }}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="attrition-category-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={`This page may be out of date: ${error.message}`}
            />
          ) : !record.isActive ? (
            <ProfileBanner
              id="attrition-category-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This category is deleted. It is not offered when a request is filed; the requests already filed under it keep it."
            />
          ) : record.documents.length === 0 ? (
            <ProfileBanner
              id="attrition-category-profile-banner"
              tone="amber"
              icon={<TriangleAlert className="h-4 w-4" aria-hidden="true" />}
              message="This category has no documents on its checklist, so no request can be filed under it yet."
              action={editChecklist}
            />
          ) : stranded.length > 0 ? (
            <ProfileBanner
              id="attrition-category-profile-banner"
              tone="amber"
              icon={<TriangleAlert className="h-4 w-4" aria-hidden="true" />}
              message={`${stranded.length} ${stranded.length === 1 ? 'document is' : 'documents are'} reviewed by a deactivated account, so nobody can review ${stranded.length === 1 ? 'it' : 'them'}.`}
              action={editChecklist}
            />
          ) : (
            <ProfileBanner
              id="attrition-category-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="A request filed under this category asks for every document on its checklist."
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="attrition-category-profile-details"
          title="Details"
          onEdit={() => setPending('edit')}
          editPermission="attrition-categories.update"
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: record.name },
              { label: 'Description', value: record.description },
              { label: 'Order', value: String(record.sortOrder), mono: true },
            ]}
          />
        </ProfileCard>

        {/*
          The checklist, read back. It is edited on the category's own form — the Details card's
          Edit, one modal for one record, the disbursement request's Items-tab arrangement — so it
          carries no second Edit. The banner owns the way out when it is empty.
        */}
        <ProfileCard
          id="attrition-category-profile-checklist"
          title="Document checklist"
          count={record.documents.length}
          empty={record.documents.length === 0 ? { title: 'No documents on the checklist yet' } : undefined}
        >
          {record.documents.length === 0 ? null : (
            <PreviewTable
              variant="table-display"
              rows={record.documents.map((document) => ({
                label: document.name,
                value: (
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span>
                      Reviewed by {document.reviewerName ?? 'an account no longer listed'}
                      {document.reviewerIsActive ? null : (
                        <span className="ml-2 text-xs text-red-700">Account deactivated</span>
                      )}
                    </span>
                    {document.description ? (
                      <span className="text-xs text-slate-400">{document.description}</span>
                    ) : null}
                  </span>
                ),
              }))}
            />
          )}
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="attrition-category-profile-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: record.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(record.createdAt)} className="font-mono">
                    {formatDateTime(record.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: record.updatedByName },
              {
                label: 'Updated',
                value:
                  record.updatedAt != null ? (
                    <time dateTime={toIso(record.updatedAt)} className="font-mono">
                      {formatDateTime(record.updatedAt)}
                    </time>
                  ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {pending === 'edit' ? (
        <AttritionCategoryModal
          open
          editing={record}
          onClose={() => setPending(null)}
          onSaved={(saved) => {
            setCategory(saved);
            setPending(null);
          }}
        />
      ) : null}

      {pending === 'delete' || pending === 'restore' ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={pending === 'delete' ? 'Delete category' : 'Restore category'}
          danger={pending === 'delete'}
          requireConfirm={pending === 'delete'}
          requireCredential={pending === 'delete'}
          loading={running}
          id={`attrition-category-profile-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {pending === 'delete'
                  ? `"${record.name}" stops being offered when a request is filed. The ${record.requestCount} filed under it keep it.`
                  : `"${record.name}" is offered again when a request is filed.`}
              </p>
              <p>Nothing is erased — a deleted category can be restored.</p>
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="attrition-categories"
        entityId={record.id}
      />
    </div>
  );
}
